// ./fileManagerを汚したくないのでこちらをいじることとする

import { newDefaultData } from '@/appLogic/importData/initialData';

import { TranslateExcelBusiness, isValidTranslateBusiness, newExcelMapper } from '@/coreDomainsModule/translateExcel';
import { isSalesResults, initSalesResults, assertParticalSalesResults } from '@/coreDomainsModule/entities/salesResults';
import { IAnnounce, IAnnounceDaily } from '@/coreDomainsModule/entities/forecast';
import { isArrivals, initArrivals, assertParticalArrivals } from '@/coreDomainsModule/entities/arrival';
import { IYearMonth } from '@/coreDomainsModule/date';
import XLSX from 'xlsx';
import _ from 'lodash';
import { simpleCopy } from '@/utils/deepCopy';
import { consoleObject } from '@/utils/consoleOutput';
import {
  ARRIVAL_INITIAL_VALUE,
  FORECAST_INITIAL_VALUE,
  SALES_RESULTS_INITIAL_VALUE,
  STOCK_INITIAL_VALUE,
  ENGLISH_MONTH_TO_NUMBER,
} from '@/coreDomainsModule/constants';
import {
  ExcelYearMonth,
  TranslateKeyName,
  ExcelRow,
  ExcelTranslateError,
  ExcelParseOption,
  ExcelRegisteredFileInfo,
  AnnounceExcelMapper,
} from '../../../coreDomainsModule/translateExcel';
import {
  addFileNameToInputExcelOption,
  defaultAnnounceExcelParseOption,
  defaultArrivalExcelParseOption,
  defaultInventoryExcelParseOption,
  defaultSalesResultExcelParseOption,
} from '@/coreDomainsModule/excelParseOptions';
import { filterStringNumberUndefinedToString } from '@/coreDomainsModule/extends/string';
import { getTranslateBusinessIdManager } from '@/utils/translateBusinessIdManager';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { fm } from '@/appLogic/fileManager/baseFileManager';
import {
  newValidateInputFoundationParameters,
  validateInputAnnounceParameters,
  validateInputAnnounceDailyParameters,
  validateInputArrivalParameters,
  validateInputFoundationParameters,
  validateInputSalesResultsParameters,
  validateInputStockParameters,
} from './validateTranslateExcel';
import { AlterableHeader, InputFileType, RegisterExcelInfo } from '@/coreDomainsModule/entities/inputExcelInfo';
import { bringCellValue, checkYearsInfoExists, columnToNumber, fetchRowIndex } from './excelUtils';
import { registerErrorInformation } from './excelValidates';
import {
  ANNOUNCE_EXCEL_START_CELL,
  DAILY_ANNOUNCE_START_DAY_CELL,
  EXCEL_SERIAL_DATE_MIN,
} from '@/appLogic/fileManager/translate/constants/importExcelConstants';

const date = new Date();

/**
 * ファイル読み込みに関するロジックをまとめる
 */
export class TranslateInputFileManager {
  private latestYearMonthData: ExcelYearMonth = {
    // TODO: 一時的にフラグでリセット管理を行う。errorlog出力処理を分離後、そちらへ移植したい。
    // resetAll(); // 呼び出し元のState諸共初期化する
    // TODO: latestYearMonthDataをグローバルで持たせない方法を取るor監視による初期化(インジェクトプロバイダ、vuex等)の実装にしたい
    // TODO: ↑が難しい場合、初期化処理を行う適切なタイミングを模索し、実装しなおす
    // エラーログに出力される列名を決定する際、列名(エクセルの書式決定)=>数値の手順で行っている
    // エラーログには転記に失敗した(転記先に情報がないかを判断してダメだった)情報が出力される
    // 転記に失敗した情報は列名生成後に行われるため、同じ関数スコープ内でlatestYearMonthDataと同等のものの生成を行うことが難しかった
    // そのため、グローバル変数でlatestYearMonthDataを定義して、エクセルの出力終わりに初期化するようにした
    // ただし、途中で処理に失敗すると、この初期化処理は通らずバグの原因となってしまうため、別の場所で行う等して改善する必要がある
    customerAnnounce: [],
  };

  public getLatestYearMonthData = (): ExcelYearMonth => {
    return this.latestYearMonthData;
  };

  public setLatestYearMonthData = (data: ExcelYearMonth): void => {
    this.latestYearMonthData = data;
  };

  public init = (): void => {
    this.latestYearMonthData = {
      customerAnnounce: [],
    };
  };

  /**
   * 内示ファイルの英語ヘッダーから日本語ヘッダーへのマッピング
   * NOTE: 受領用内示出力の英語ヘッダーと同じです
   */
  private static readonly ENGLISH_TO_JAPANESE_HEADER_MAP: Record<string, string> = {
    customercode: '売上先CD',
    customername: '売上先名',
    vendorcode: '仕入先CD',
    vendorname: '仕入先名',
    packingunit: '容量',
    materialcode: '商品CD',
    storagelocationcode: '置場CD',
    storagelocationname: '置場名',
    shiptopartycode: '送り先CD',
    shiptopartyname: '送り先名',
    materialname: '商品名',
    manufacturer: '製造社',
    enduser: 'エンドユーザー',
  };

  /**
   * 英語の年月フォーマットをアンダースコア形式に変換する (例: "2026Year" -> "2026", "Jan." -> "1")
   * @param headerValue - ヘッダー文字列
   * @returns 変換後の値、または変換できない場合は元の値
   */
  private normalizeEnglishYearMonth = (headerValue: string): string => {
    const trimmedValue = headerValue.trim().toLowerCase();

    // Check for English year format (e.g., "2026Year", "2026year")
    const yearMatch = trimmedValue.match(/^(\d{4})year$/i);
    if (yearMatch) {
      return yearMatch[1]; // Return just the year number
    }

    // Check for English month format (e.g., "Jan.", "January")
    const monthNumber = ENGLISH_MONTH_TO_NUMBER[trimmedValue];
    if (monthNumber !== undefined) {
      return monthNumber.toString();
    }

    return headerValue;
  };

  /**
   * 内示系のヘッダー名を正規化する
   * @param headerValue - ヘッダー文字列
   * @param excelType - エクセル種別
   * @returns 正規化後のヘッダー文字列
   */
  private normalizeAnnounceHeader = (headerValue: string, excelType: string): string => {
    if (excelType !== '内示' && excelType !== '日別内示') return headerValue;

    const trimmedHeaderValue = headerValue.trim();
    const normalizedHeaderValue = trimmedHeaderValue.replace(/\s/g, '');

    // Japanese header keys
    const headerKeys = [
      '売上先CD',
      '売上先名',
      '仕入先CD',
      '仕入先名',
      '容量',
      '商品CD',
      '置場CD',
      '置場名',
      '送り先CD',
      '送り先名',
      '商品名',
      '製造社',
      'エンドユーザー',
    ];

    // Check Japanese headers first
    for (const key of headerKeys) {
      if (normalizedHeaderValue.startsWith(key)) return key;
    }

    // Check English headers and convert to Japanese
    const lowerCaseHeader = normalizedHeaderValue.toLowerCase();
    const japaneseEquivalent = TranslateInputFileManager.ENGLISH_TO_JAPANESE_HEADER_MAP[lowerCaseHeader];
    if (japaneseEquivalent) {
      return japaneseEquivalent;
    }

    // Try to normalize English year/month format
    const normalizedYearMonth = this.normalizeEnglishYearMonth(trimmedHeaderValue);
    if (normalizedYearMonth !== trimmedHeaderValue) {
      return normalizedYearMonth;
    }

    return trimmedHeaderValue;
  };

  /**
   * 日別内示のヘッダーから年月日キーを作成する
   * @param headerValue - ヘッダー文字列
   * @returns 年月日キー、作成できない場合はundefined
   */
  private createDailyAnnounceKeyFromHeader = (headerValue: string): string | undefined => {
    const serialCandidate = Number(headerValue);
    if (Number.isFinite(serialCandidate) && serialCandidate >= EXCEL_SERIAL_DATE_MIN) {
      const date = DateService.convertExcelSerialToDate(serialCandidate);
      if (date) return `${date.year}_${date.month}_${date.day}`;
    }

    const normalizedHeaderValue = headerValue.replace(/\s/g, '');
    const dateMatch = normalizedHeaderValue.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (!dateMatch) return;

    const [, year, month, day] = dateMatch;
    const yearNum = Number(year);
    const monthNum = Number(month);
    const dayNum = Number(day);

    if (Number.isNaN(yearNum) || Number.isNaN(monthNum) || Number.isNaN(dayNum)) return;
    return `${yearNum}_${monthNum}_${dayNum}`;
  };

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * 内示をIBusinessに取り込む
   * TranslateExcelBusiness[] -> TranslateExcelBusiness & Announce にする
   * @param businesses IBusinessをラップしたエクセル転記情報
   * @param excelData 1行のエクセルデータ
   * @param fileReadErrors エラーをまとめるためのオブジェクト
   * @param useValidate バリデーションを行うかどうか
   * @param receivedYearMonth - 受領年月
   * @returns 内示をIBusinessに直したものまたはundefinedを返す
   */
  public ANNOUNCE_MAPPER: AnnounceExcelMapper = (
    businesses: TranslateExcelBusiness,
    excelData: ExcelRow,
    fileReadErrors: ExcelTranslateError[],
    useValidate: boolean,
    receivedYearMonth?: IYearMonth
  ): ExcelRow | undefined => {
    const targetBusiness: TranslateExcelBusiness = businesses;
    // formArray[idx].salesPosition = 'seller';
    const announceArr: IAnnounce[] = [];
    // エクセルで読んだシートのデータから、内示情報が入力されている列のデータをフィルタリングする
    // 同時に「前月実績」などのX月のX部分に文字列が含まれていないデータもフィルタリングする
    // 183行目 .filter((r) => r.includes('月') && !String(excelData[r].address).includes('I')) の方がいい？
    const filteredExcelData: ExcelRow = Object.keys(excelData)
      .filter((r) => DateService.isYearMonthUnderscoreFormat(r))
      .reduce((newExcelData: ExcelRow, r) => {
        // keyから'年月'を除去し、'20YY_MM'のような形でエラーログ用の内示年月の一覧に追加
        const yearMonth = r.replace(/[年月]/g, '');
        // TODO: 年情報がエクセルに記入されていない場合はエラーを表示するようにする
        if (!this.latestYearMonthData.customerAnnounce.includes(yearMonth) && r.split('_')[0] !== '') {
          this.latestYearMonthData.customerAnnounce.push(yearMonth);
        }
        newExcelData[r] = excelData[r];
        return newExcelData;
      }, {});

    // 最も若い年月を保持する
    let mostEarliestYearMonth: IYearMonth = { year: 9999, month: 12 };

    for (const r of Object.keys(filteredExcelData)) {
      // エクセルで読んだ月の順序が保証されていない
      //  TODO: 本当はn月みたいに正規表現で取らなければいけない
      // TODO: 時間がなかったため、こんな雑な処理にしている。なんとかしたい。（正規表現使うなりで）
      const year = r.split('_')[0].substring(0, 4) ?? -1;
      const month = DateService.convertMonthWithZero(r.split('_')[1]) ?? -1;
      let inputValue = String(filteredExcelData[r].value);
      if (inputValue === '' || inputValue === 'undefined') {
        // 空文字の時は、初期値を代入する形で処理する（最終的に空文字で処理するため）
        inputValue = String(FORECAST_INITIAL_VALUE);
      }
      const announceValue = inputValue.removeComma();

      if (useValidate) {
        const excelData = filteredExcelData[r];
        validateInputAnnounceParameters(
          {
            year: Number(year),
            month: Number(month),
            amount: Number(announceValue),
            manageInitiatedYearMonth: undefined,
            createAt: undefined,
            updateAt: undefined,
          },
          excelData?.fileName?.toString() ?? '',
          excelData.sheetName?.toString() ?? '',
          excelData.address?.toString() ?? '',
          fileReadErrors
        );
      }

      const prevAnnounceValue =
        targetBusiness?.customerAnnounce?.find((item) => {
          return item.year === Number(year) && item.month === Number(month);
        })?.amount ?? undefined; // NOTE: 1行に複数の内示行がある場合、valueは合算してあげるように

      // 月と曜日はここから取得して入れる
      announceArr.push({
        year: Number(year),
        month: Number(month),
        amount: (prevAnnounceValue ?? 0) + Number(announceValue),
        manageInitiatedYearMonth: undefined,
        createAt: undefined,
        updateAt: undefined,
      });

      // 最も若い対象月を判定し格納する
      const targetYearMonth: IYearMonth = { year: Number(year), month: Number(month) };
      if (DateService.compareYearMonths(targetYearMonth, mostEarliestYearMonth) === -1) mostEarliestYearMonth = targetYearMonth;
    }

    // 最も若い対象月の一つ前の月を受領月とする
    const shiftedEarliestYearMonth = DateService.getShiftedIYearMonthByMonths(mostEarliestYearMonth.year, mostEarliestYearMonth.month, -1);

    // 今回の計算した受領月がエクセル全体の受領月になるかどうか（＝最も若いか）を判定する
    if (receivedYearMonth && DateService.compareYearMonths(shiftedEarliestYearMonth, receivedYearMonth) === -1) {
      receivedYearMonth.year = shiftedEarliestYearMonth.year;
      receivedYearMonth.month = shiftedEarliestYearMonth.month;
    }

    // setAnnounceYearMonth(announceArr, true); // 他のエクセルと同様に年月を区別するようにしたため一旦必要なくなった。
    targetBusiness.customerAnnounce = announceArr;
    return;
  };

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * 日別内示をIBusinessに取り込む
   * TranslateExcelBusiness[] -> TranslateExcelBusiness & AnnounceDaily にする
   * @param businesses IBusinessをラップしたエクセル転記情報
   * @param excelData 1行のエクセルデータ
   * @param fileReadErrors エラーをまとめるためのオブジェクト
   * @param useValidate バリデーションを行うかどうか
   * @param receivedYearMonth - 受領年月
   * @returns 日別内示をIBusinessに直したものまたはundefinedを返す
   */
  public ANNOUNCE_DAILY_MAPPER: AnnounceExcelMapper = (
    businesses: TranslateExcelBusiness,
    excelData: ExcelRow,
    fileReadErrors: ExcelTranslateError[],
    useValidate: boolean,
    receivedYearMonth?: IYearMonth
  ): ExcelRow | undefined => {
    const targetBusiness: TranslateExcelBusiness = businesses;
    const announceArr: IAnnounceDaily[] = [];
    const filteredExcelData: ExcelRow = Object.keys(excelData)
      .filter((r) => DateService.isYearMonthDayUnderscoreFormat(r))
      .reduce((newExcelData: ExcelRow, r) => {
        newExcelData[r] = excelData[r];
        return newExcelData;
      }, {});

    let mostEarliestYearMonth: IYearMonth = { year: 9999, month: 12 };

    for (const r of Object.keys(filteredExcelData)) {
      const year = r.split('_')[0].substring(0, 4) ?? -1;
      const month = DateService.convertMonthWithZero(r.split('_')[1]) ?? -1;
      const day = DateService.convertMonthWithZero(r.split('_')[2]) ?? -1;
      let inputValue = String(filteredExcelData[r].value);
      if (inputValue === '' || inputValue === 'undefined') {
        inputValue = String(FORECAST_INITIAL_VALUE);
      }
      const announceValue = inputValue.removeComma();

      if (useValidate) {
        const excelData = filteredExcelData[r];
        validateInputAnnounceDailyParameters(
          {
            year: Number(year),
            month: Number(month),
            day: Number(day),
            amount: Number(announceValue),
            manageInitiatedYearMonth: undefined,
            createAt: undefined,
            updateAt: undefined,
          },
          excelData?.fileName?.toString() ?? '',
          excelData.sheetName?.toString() ?? '',
          excelData.address?.toString() ?? '',
          fileReadErrors
        );
      }

      const prevAnnounceValue =
        targetBusiness?.customerAnnounceDaily?.find((item) => {
          return item.year === Number(year) && item.month === Number(month) && item.day === Number(day);
        })?.amount ?? undefined;

      announceArr.push({
        year: Number(year),
        month: Number(month),
        day: Number(day),
        amount: (prevAnnounceValue ?? 0) + Number(announceValue),
        manageInitiatedYearMonth: undefined,
        createAt: undefined,
        updateAt: undefined,
      });

      const targetYearMonth: IYearMonth = { year: Number(year), month: Number(month) };
      if (DateService.compareYearMonths(targetYearMonth, mostEarliestYearMonth) === -1) mostEarliestYearMonth = targetYearMonth;
    }

    const shiftedEarliestYearMonth = DateService.getShiftedIYearMonthByMonths(mostEarliestYearMonth.year, mostEarliestYearMonth.month, -1);
    if (receivedYearMonth && DateService.compareYearMonths(shiftedEarliestYearMonth, receivedYearMonth) === -1) {
      receivedYearMonth.year = shiftedEarliestYearMonth.year;
      receivedYearMonth.month = shiftedEarliestYearMonth.month;
    }

    targetBusiness.customerAnnounceDaily = announceArr;
    return;
  };

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * 発注済み数量をIBusinessに取り込む
   * TranslateExcelBusiness[] -> TranslateExcelBusiness & Arrival にする
   * @param businesses IBusinessをラップしたエクセル転記情報
   * @param excelData 1行のエクセルデータ
   * @param inputExcelInfo エクセル情報
   * @param fileReadErrors エラーをまとめるためのオブジェクト
   * @param useValidate バリデーションを行うかどうか
   * @returns 発注済み数量をIBusinessに直したものまたはundefinedを返す
   */
  public ARRIVAL_MAPPER: newExcelMapper = (
    businesses: TranslateExcelBusiness,
    excelData: ExcelRow,
    inputExcelInfo: RegisterExcelInfo,
    fileReadErrors: ExcelTranslateError[],
    useValidate: boolean
  ): ExcelRow | undefined => {
    const yearAndMonth = /^([0-9]{4})([0-9]{1,2})$/;
    const targetBusiness: TranslateExcelBusiness = businesses;

    if (!isArrivals(targetBusiness)) {
      initArrivals(targetBusiness);
    }
    assertParticalArrivals(targetBusiness);
    if (!isSalesResults(targetBusiness)) {
      initSalesResults(targetBusiness);
    }
    assertParticalSalesResults(targetBusiness);

    let matchCount = 0;
    let index = -1;
    const yearMonthValues = this.getYearMonthValueForExcelInfo(excelData, 'Arrival', inputExcelInfo);

    for (const yearMonthValue of yearMonthValues) {
      index++;
      matchCount++; // yearMonthValueが存在する場合は、matchCountをインクリメントする

      // カンマ付き数字に対応
      let valueNumber = String(yearMonthValue?.columnValue?.value).removeComma();
      if (valueNumber === '' || valueNumber === 'undefined') {
        // 空文字の時は、初期値を代入する形で処理する（最終的に空文字で処理するため）
        valueNumber = String(ARRIVAL_INITIAL_VALUE);
      }

      const yearAndMonthMatched = yearMonthValue.yearMonthString.match(yearAndMonth);

      if (!yearAndMonthMatched) {
        if (useValidate) {
          validateInputArrivalParameters(
            Number(valueNumber),
            yearMonthValue.columnValue?.fileName?.toString() ?? '',
            yearMonthValue.columnValue.sheetName?.toString() ?? '',
            yearMonthValue.columnValue.address?.toString() ?? '',
            true,
            false,
            true,
            fileReadErrors
          );
        }
        continue;
      }

      if (useValidate) {
        validateInputArrivalParameters(
          Number(valueNumber),
          yearMonthValue.columnValue?.fileName?.toString() ?? '',
          yearMonthValue.columnValue.sheetName?.toString() ?? '',
          yearMonthValue.columnValue.address?.toString() ?? '',
          false,
          false,
          true,
          fileReadErrors
        );
      }

      const [, year, month] = yearAndMonthMatched;
      const yearMonthInfo: IYearMonth = {
        year: Number(year),
        month: Number(month),
      };

      const alreadyExistArrivalIndex = targetBusiness.arrivals.findIndex((item) => DateService.equalYearMonthInfo(item, yearMonthInfo));
      if (alreadyExistArrivalIndex !== -1) {
        targetBusiness.arrivals[alreadyExistArrivalIndex] = {
          arrivalId: '',
          year: Number(year),
          month: Number(month),
          amount: targetBusiness.arrivals[alreadyExistArrivalIndex].amount + Number(valueNumber),
          day: targetBusiness.arrivals[alreadyExistArrivalIndex].day,
          leadTime: targetBusiness.arrivals[alreadyExistArrivalIndex].leadTime,
          isFixOrdered: undefined,
          fixOrderDate: undefined,
          crossDate: undefined,
          status: undefined,
          memoText: undefined,
          partialDeliveries: undefined,
          number: undefined,
          updateAt: undefined,
        };
      } else {
        targetBusiness.arrivals.push({
          arrivalId: '',
          year: Number(year),
          month: Number(month),
          amount: Number(valueNumber),
          leadTime: 0,
          day: 0,
          isFixOrdered: undefined,
          fixOrderDate: undefined,
          crossDate: undefined,
          status: undefined,
          memoText: undefined,
          partialDeliveries: undefined,
          number: undefined,
          updateAt: undefined,
        });
      }
    }

    if (matchCount === 0) {
      if (useValidate) {
        validateInputArrivalParameters(
          ARRIVAL_INITIAL_VALUE,
          excelData?.fileName?.toString() ?? '',
          excelData.sheetName?.toString() ?? '',
          excelData.address?.toString() ?? '',
          false,
          true,
          false,
          fileReadErrors
        );
      }
    }

    /**
     * yearmonthで並び替え
     */
    targetBusiness.arrivals.sort((a, b) => {
      const aStr = String(a.year) + (String(a.month).length == 1 ? '0' + String(a.month) : String(a.month));
      const bStr = String(b.year) + (String(b.month).length == 1 ? '0' + String(b.month) : String(b.month));
      return aStr < bStr ? -1 : 1;
    });
    targetBusiness.salesResults.sort((a, b) => {
      const aStr = String(a.year) + (String(a.month).length == 1 ? '0' + String(a.month) : String(a.month));
      const bStr = String(b.year) + (String(b.month).length == 1 ? '0' + String(b.month) : String(b.month));
      return aStr < bStr ? -1 : 1;
    });
    return;
  };

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * 販売履歴数量をIBusinessに取り込む
   * TranslateExcelBusiness[] -> TranslateExcelBusiness & SalesResult にする
   * @param businesses IBusinessをラップしたエクセル転記情報
   * @param excelData 1行のエクセルデータ
   * @param inputExcelInfo エクセルの読み替え情報
   * @param fileReadErrors エラーをまとめるためのオブジェクト
   * @param useValidate バリデーションを行うかどうか
   * @returns 販売履歴数量をIBusinessに直したものまたはundefinedを返す
   */
  public SALES_RESULTS_MAPPER: newExcelMapper = (
    businesses: TranslateExcelBusiness,
    excelData: ExcelRow,
    inputExcelInfo: RegisterExcelInfo,
    fileReadErrors: ExcelTranslateError[],
    useValidate: boolean
  ): ExcelRow | undefined => {
    const yearAndMonth = /^([0-9]{4})([0-9]{1,2})$/;
    const endOfMonth = /([0-9]{1,2})月末\s*(.*)/;
    const targetBusiness: TranslateExcelBusiness = businesses;
    if (!isArrivals(targetBusiness)) {
      initArrivals(targetBusiness);
    }
    assertParticalArrivals(targetBusiness);
    if (!isSalesResults(targetBusiness)) {
      initSalesResults(targetBusiness);
    }
    assertParticalSalesResults(targetBusiness);

    let matchCount = 0;
    let index = -1;
    const yearMonthValues = this.getYearMonthValueForExcelInfo(excelData, 'SalesResults', inputExcelInfo);

    for (const yearMonthValue of yearMonthValues) {
      index++;
      matchCount++; // yearMonthValueが存在する場合は、matchCountをインクリメントする

      // カンマ付き数字に対応
      let valueNumber = String(yearMonthValue?.columnValue?.value).removeComma();
      if (valueNumber === '' || valueNumber === 'undefined') {
        // 空文字の時は、初期値を代入する形で処理する（最終的に空文字で処理するため）
        valueNumber = String(SALES_RESULTS_INITIAL_VALUE);
      }
      const yearAndMonthMatched = yearMonthValue.yearMonthString.match(yearAndMonth);
      if (!yearAndMonthMatched) {
        if (useValidate) {
          validateInputSalesResultsParameters(
            Number(valueNumber),
            yearMonthValue.columnValue?.fileName?.toString() ?? '',
            yearMonthValue.columnValue.sheetName?.toString() ?? '',
            yearMonthValue.columnValue.address?.toString() ?? '',
            true,
            false,
            true,
            fileReadErrors
          );
        }
        continue;
      }

      if (useValidate) {
        validateInputSalesResultsParameters(
          Number(valueNumber),
          yearMonthValue.columnValue?.fileName?.toString() ?? '',
          yearMonthValue.columnValue.sheetName?.toString() ?? '',
          yearMonthValue.columnValue.address?.toString() ?? '',
          false,
          false,
          true,
          fileReadErrors
        );
      }

      const [, year, month] = yearAndMonthMatched;
      const yearMonthInfo: IYearMonth = {
        year: Number(year),
        month: Number(month),
      };

      const alreadyExistArrivalIndex = targetBusiness.salesResults.findIndex((item) => DateService.equalYearMonthInfo(item, yearMonthInfo));
      if (alreadyExistArrivalIndex !== -1) {
        targetBusiness.salesResults[alreadyExistArrivalIndex] = {
          year: Number(year),
          month: Number(month),
          amount: (targetBusiness.salesResults[alreadyExistArrivalIndex].amount ?? 0) + Number(valueNumber),
        };
      } else {
        targetBusiness.salesResults.push({ year: Number(year), month: Number(month), amount: Number(valueNumber) });
      }
    }

    if (matchCount === 0) {
      if (useValidate) {
        validateInputSalesResultsParameters(
          SALES_RESULTS_INITIAL_VALUE,
          excelData?.fileName?.toString() ?? '',
          excelData.sheetName?.toString() ?? '',
          excelData.address?.toString() ?? '',
          false,
          true,
          false,
          fileReadErrors
        );
      }
    }
    /**
     * yearmonthで並び替え
     */
    targetBusiness.arrivals.sort((a, b) => {
      const aStr = String(a.year) + (String(a.month).length == 1 ? '0' + String(a.month) : String(a.month));
      const bStr = String(b.year) + (String(b.month).length == 1 ? '0' + String(b.month) : String(b.month));
      return aStr < bStr ? -1 : 1;
    });
    targetBusiness.salesResults.sort((a, b) => {
      const aStr = String(a.year) + (String(a.month).length == 1 ? '0' + String(a.month) : String(a.month));
      const bStr = String(b.year) + (String(b.month).length == 1 ? '0' + String(b.month) : String(b.month));
      return aStr < bStr ? -1 : 1;
    });
    return;
  };

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * 在庫をIBusinessに取り込む
   * @param businesses IBusinessをラップしたエクセル転記情報
   * @param excelData 1行のエクセルデータ
   * @param inputExcelInfo エクセルの読み替え情報
   * @param fileReadErrors エラーをまとめるためのオブジェクト
   * @param useValidate バリデーションを行うかどうか
   * @returns 在庫をIBusinessに直したものまたはundefinedを返す
   */
  public STOCK_MAPPER: newExcelMapper = (
    businesses: TranslateExcelBusiness,
    excelData: ExcelRow,
    inputExcelInfo: RegisterExcelInfo,
    fileReadErrors: ExcelTranslateError[],
    useValidate: boolean
  ): ExcelRow | undefined => {
    const targetBusiness: TranslateExcelBusiness = businesses;
    const yearAndMonth = /^([0-9]{4})([0-9]{1,2})$/;

    let matchCount = 0;
    let index = -1;
    const yearMonthValues = this.getYearMonthValueForExcelInfo(excelData, 'Inventory', inputExcelInfo);
    for (const yearMonthValue of yearMonthValues) {
      index++;
      matchCount++; // yearMonthValueが存在する場合は、matchCountをインクリメントする
      let valueNumber = String(yearMonthValue?.columnValue?.value).removeComma();
      if (valueNumber === '' || valueNumber === 'undefined') {
        // 空文字の時は、初期値を代入する形で処理する（最終的に空文字で処理するため）
        valueNumber = String(STOCK_INITIAL_VALUE);
      }

      const yearAndMonthMatched = yearMonthValue.yearMonthString.match(yearAndMonth);

      if (!yearAndMonthMatched) {
        if (useValidate) {
          validateInputStockParameters(
            Number(valueNumber),
            yearMonthValue.columnValue?.fileName?.toString() ?? '',
            yearMonthValue.columnValue.sheetName?.toString() ?? '',
            yearMonthValue.columnValue.address?.toString() ?? '',
            true,
            false,
            true,
            fileReadErrors
          );
        }
        continue;
      }

      if (useValidate) {
        validateInputStockParameters(
          Number(valueNumber),
          yearMonthValue.columnValue?.fileName?.toString() ?? '',
          yearMonthValue.columnValue.sheetName?.toString() ?? '',
          yearMonthValue.columnValue.address?.toString() ?? '',
          false,
          index === Object.keys(excelData).length - 1 && matchCount === 0, // 最後の行まで行っても、マッチしていない場合はエラーを出す
          true,
          fileReadErrors
        );
      }

      const [, year, month] = yearAndMonthMatched;

      // 在庫エクセルから読み取った情報からmvpInventoryを作成する
      // 無い場合は空配列を入れる
      if (!targetBusiness.mvpInventory) {
        targetBusiness.mvpInventory = [];
      }

      // 在庫情報を作成
      targetBusiness.mvpInventory.push({
        year: Number(year),
        month: Number(month),
        amount: Number(valueNumber),
      });
    }

    if (matchCount === 0) {
      if (useValidate) {
        validateInputStockParameters(
          STOCK_INITIAL_VALUE,
          excelData?.fileName?.toString() ?? '',
          excelData.sheetName?.toString() ?? '',
          excelData.address?.toString() ?? '',
          false,
          true,
          false,
          fileReadErrors
        );
      }
    }
    return;
  };

  /**
   * 表の位置が不定なExcelの中から、ExcelRow[]を作成する関数
   * @param workSheet ワークシートの情報
   * @param startCell 表が始まっているセル番号
   * @param excelType 解析するエクセルの種類
   * @param option parseExcelObjectsで色々やるためのオプション
   * @param errorObj
   */
  public parseAnnounceExcelObjects = (
    workSheet: XLSX.WorkSheet,
    startCell: string,
    excelType: string,
    option?: ExcelParseOption,
    errorObj?: ExcelTranslateError[]
  ): ExcelRow[] => {
    //セルの範囲
    const output: ExcelRow[] = [];
    const headerStrings: string[] = []; // 重複を許可したいため配列にしてます
    const startCellVal = XLSX.utils.decode_cell(startCell);
    const separateYmdData: string[] = [];
    const range = workSheet['!ref'];
    let findHeaderRow = -1;
    const ignoreRows: Array<number> = [];
    // convert index
    for (const row of option ? option.ignoreRow : []) {
      ignoreRows.push(XLSX.utils.decode_cell(row).r);
    }
    if (!range) return [];
    const rangeVal = XLSX.utils.decode_range(range);

    // 0-indexとn-indexの差を埋める（表のはじまっているシートが基本0,0の位置のため0-index)
    const baseColumn = startCellVal.c;

    // console.log('一旦', { range: range, startCellVal: startCellVal });
    // 実績部分がN個ある形式に対応
    if (excelType === '販売実績' || excelType === '入荷実績' || excelType === '内示' || excelType === '日別内示') {
      // 年月のスタートセルの座標separateYmdと区切り数mを受け取る
      // [yyyy/mm, ...,] m個区切りで.length分のカラムがある
      // memo 17個ずつ配置されてる
      if (option) {
        const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);
        for (let c = startAddress.c; c <= rangeVal.e.c; c = c + option.separateYmd.separateCount) {
          const adr = XLSX.utils.encode_cell({ c: c, r: startAddress.r });
          const cell = workSheet[adr];
          const cellValue = (bringCellValue(cell).parseNumber() || '').toString();
          separateYmdData.push(cellValue);
        }
      }
      // エクセルの取引数量の列に年情報が記入されていない場合の処理
      // この場合はフォーマットエラーとして扱い、処理を途中で中断してファイル名を変数に入れる
      if (!checkYearsInfoExists(separateYmdData)) {
        // errorをまとめるオブジェクトに追加する
        errorObj?.push({
          key: '',
          errCode: 'InappropriateType',
          errorFileName: {
            inputFileName: String(option?.fileName),
          },
          preCellNumber: '',
          errorCellNumber: {
            inputCellNumber: '',
            registerCellNumber: '',
          },
        });
        return [];
      }
    }
    // workSheet['!ref']がpaddingのような切り取り方をしているのでとりあえずmax-1行目までの抽出としています
    for (let r = 0; r <= rangeVal.e.r; r++) {
      if (ignoreRows.includes(r)) continue;
      const rowInfo: ExcelRow = Object.assign({}, {});
      let updatedRow = false;
      for (let c = 0; c <= rangeVal.e.c; c++) {
        const adr = XLSX.utils.encode_cell({ c: c, r: r });
        const cell = workSheet[adr];
        const cellValue = bringCellValue(cell);
        // 対象範囲
        if (startCellVal.c <= c && startCellVal.r <= r) {
          if (findHeaderRow == -1 || findHeaderRow == r) {
            headerStrings.push(cellValue); // ヘッダーの追加
            findHeaderRow = r; // 最初の１行目だけ見たい（Header)
            // console.log(`${adr} type:${cell.t} value:${cell.v} text:${cell.w}`);
            continue;
          }
          // 空として登録されてるヘッダーについては無視する(セルが結合等されていてindexずれしていたのがうざかったのでこうしています)
          // TODO: ここ（分岐）、セル結合を考慮して何か処理を入れないといけない気がしているがうまくいかないのでとりあえずこうしてます
          if (true) {
            // columnIndexは1-indexとして扱いたい
            const headerValue = String(headerStrings[c - baseColumn] ?? '');
            const normalizedHeaderValue = this.normalizeAnnounceHeader(headerValue, excelType);
            const dailyKey = excelType === '日別内示' ? this.createDailyAnnounceKeyFromHeader(normalizedHeaderValue) : undefined;
            const rawKey = dailyKey ?? fetchRowIndex(normalizedHeaderValue, c, separateYmdData, option);
            // console.log('チェック', headerStrings, baseColumn + c, { cellValue: cellValue, rawKey });
            if (!rowInfo?.[rawKey]) {
              // 既に同じキーが存在している場合は無視する
              rowInfo[rawKey] = {
                value: cellValue,
                address: adr,
                sheetName: option?.sheetName,
                fileName: option?.fileName,
              };
            }
            updatedRow = true; // 1つでもデータが入っていたら更新フラグを立てる
          }
        }
        // console.log(`${adr} type:${cell.t} value:${cell.v} text:${cell.w}`);
      }
      if (updatedRow) {
        output.push(rowInfo);
      }
    }
    return output;
  };

  /**
   * エクセルファイルをIBusiness型に落とし込む
   * @param excelData 解析済みエクセルデータ
   * @param formArray IBusinessをラップしたエクセル転記情報の配列
   * @param readExcelName 読み込んでいるファイルの名前
   * @param errorObj エラー情報をまとめるためのオブジェクト
   * @param receivedYearMonth - 受領年月情報
   * @param option - オプション
   * @param extraMapFunc 新たにMapperがある時に利用する
   */
  public splitAnnounceExcelData = (
    excelData: ExcelRow[],
    formArray: Record<string, TranslateExcelBusiness>,
    readExcelName: TranslateKeyName,
    errorObj: ExcelTranslateError[],
    receivedYearMonth: IYearMonth,
    option?: { skipDuplicateCheck: boolean },
    ...extraMapFunc: AnnounceExcelMapper[]
  ) => {
    // 基本的な要素＋salesPositionに関してはここ、それ以外に関してはExtraMapにて処理を実行する
    excelData.forEach((data, idx) => {
      // if (idx !== 0) formArray.push(JSON.parse(JSON.stringify(newDefaultData)));
      try {
        // MVP ver1.0.0
        // 在庫表には置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDが存在する
        // 内示は内示エクセルの置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDと在庫表が合致する行に転記
        // 販売実績は販売実績エクセルの置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDと在庫表が合致する行に転記
        // 入荷は入荷エクセルの置き場CD、商品CD、容量、仕入先CDと在庫表が合致する集計行に転記
        // 在庫は在庫エクセルの置き場CD、商品CD、容量、仕入先CDと在庫表が合致する集計行に転記
        // ~名(CDと1対１対応)は、在庫表の値を採用する（転記には用いない）

        let business: TranslateExcelBusiness = JSON.parse(JSON.stringify(newDefaultData));
        business.warehouse = filterStringNumberUndefinedToString(data['置場名']?.value);
        business.warehouseNumber = filterStringNumberUndefinedToString(data['置場CD']?.value);

        business.businessName = filterStringNumberUndefinedToString(data['商品名']?.value);
        business.businessNumber = filterStringNumberUndefinedToString(data['商品CD']?.value);

        business.package = filterStringNumberUndefinedToString(data['容量']?.value).parseNumberStringAndRemoveString(); // NOTE: 容量に関しては、文字が含まれていた場合「0.01」として扱う

        business.salesDestinationName = filterStringNumberUndefinedToString(data['送り先名']?.value);
        business.salesDestinationNumber = filterStringNumberUndefinedToString(data['送り先CD']?.value);

        business.customerName = filterStringNumberUndefinedToString(data['売上先名']?.value);
        business.customerNumber = filterStringNumberUndefinedToString(data['売上先CD']?.value);

        business.supplierName = filterStringNumberUndefinedToString(data['仕入先名']?.value);
        business.supplierNumber = filterStringNumberUndefinedToString(data['仕入先CD']?.value);

        business.manufacturer = filterStringNumberUndefinedToString(data['製造社']?.value);
        business.endUser = filterStringNumberUndefinedToString(data['エンドユーザー']?.value);

        // 受領用内示メモはM列にあるので、そこから取得する
        const receiptForecastMemoPattern = /^M\d+$/;
        business.receiptForecastMemo =
          Object.values(data)
            .find((item) => receiptForecastMemoPattern.test(filterStringNumberUndefinedToString(item.address)))
            ?.value?.toString() ?? undefined;

        // IDは上記の複合キーとする
        business.translateBusinessId = getTranslateBusinessIdManager().generateBusinessIdFromExcelInput(business, readExcelName);
        const businessForUseLog = simpleCopy(business); // ログを取るためだけに、行の情報を持っておく

        // 有効な取引の基本パラメータに関して、validateを行う
        const useValidate = isValidTranslateBusiness(business);
        if (useValidate) {
          validateInputFoundationParameters(business, data, errorObj);
        }

        if (formArray[business.translateBusinessId]) {
          // 同一取引が登録されている入力側のエクセルファイル名
          if (!option?.skipDuplicateCheck) {
            let registeredExcelInfo: ExcelRegisteredFileInfo = {
              registeredFileName: '',
              registeredSheetName: '',
              registeredCellNumber: '',
            };
            const excelInfoLength = formArray[business.translateBusinessId].excelInfos?.length;
            if (excelInfoLength && (readExcelName === '内示' || readExcelName === '日別内示')) {
              registeredExcelInfo = {
                registeredFileName: formArray[business.translateBusinessId].excelInfos![excelInfoLength - 1].fileName,
                registeredSheetName: String(formArray[business.translateBusinessId].excelInfos![excelInfoLength - 1].sheetNumber),
                registeredCellNumber: String(formArray[business.translateBusinessId].excelInfos![excelInfoLength - 1].cellNumber),
              };
            }
            // 内示、販売実績のとき、同一の取引が複数入力エクセル内にあった場合に取引重複エラーとして登録する
            if (
              getTranslateBusinessIdManager().isFileDuplicated(
                readExcelName,
                formArray[business.translateBusinessId],
                business.translateBusinessId
              )
            ) {
              registerErrorInformation(
                errorObj,
                data,
                businessForUseLog,
                business.translateBusinessId,
                '取引情報が重複している',
                registeredExcelInfo
              );
            }
          }
          // 既に同一のアイテムを読み込んでいる(同一取引が重複した)場合, 既存のアイテムで更新
          business = formArray[business.translateBusinessId];
        }
        extraMapFunc.forEach((func) => func(business, data, errorObj, useValidate, receivedYearMonth));
        extraMapFunc.forEach((func) => func(businessForUseLog, data, errorObj, false));
        if (!business?.excelInfos) {
          business.excelInfos = [];
        }
        const excelInfoCellNumber = data['商品CD']?.address;
        const excelInfoSheetNumber = data['商品CD']?.sheetName;
        const excelInfoFileName = data['商品CD']?.fileName;
        const canCheckDuplicatedExcelInfo =
          excelInfoCellNumber !== undefined &&
          excelInfoCellNumber !== null &&
          excelInfoSheetNumber !== undefined &&
          excelInfoSheetNumber !== null &&
          excelInfoFileName !== undefined &&
          excelInfoFileName !== null;
        const newExcelInfo = {
          cellNumber: String(excelInfoCellNumber),
          sheetNumber: String(excelInfoSheetNumber),
          fileName: String(excelInfoFileName),
          rowObjectInfo: simpleCopy(businessForUseLog),
        };
        const duplicatedExcelInfo = canCheckDuplicatedExcelInfo
          ? business.excelInfos.find(
              (b) =>
                b.cellNumber === newExcelInfo.cellNumber &&
                b.sheetNumber === newExcelInfo.sheetNumber &&
                b.fileName === newExcelInfo.fileName
            )
          : undefined;

        if (duplicatedExcelInfo) {
          duplicatedExcelInfo.rowObjectInfo = {
            ...duplicatedExcelInfo.rowObjectInfo,
            ...newExcelInfo.rowObjectInfo,
            customerAnnounce: newExcelInfo.rowObjectInfo.customerAnnounce ?? duplicatedExcelInfo.rowObjectInfo.customerAnnounce,
            customerAnnounceDaily:
              newExcelInfo.rowObjectInfo.customerAnnounceDaily ?? duplicatedExcelInfo.rowObjectInfo.customerAnnounceDaily,
          };
        } else {
          business.excelInfos.push(newExcelInfo);
        }
        // 最後に、formArrayを更新
        formArray[business.translateBusinessId] = business;
        // キーが同じものはまとめる処理
        // 各々のインプット要素を受け取る
      } catch (error) {
        consoleObject(error);
        throw error;
      }
    });
  };

  private getYearMonthValueForExcelInfo = (
    excelData: ExcelRow,
    inputFileType: InputFileType,
    inputExcelInfo: RegisterExcelInfo
  ): {
    yearMonthString: string; // 年月の文字列(ex. 202301)
    columnValue: {
      value: string | number | undefined;
      address: string | number | undefined;
      fileName: string | number | undefined;
      sheetName: string | number | undefined;
    };
  }[] => {
    const output: {
      yearMonthString: string;
      columnValue: {
        value: string | number | undefined;
        address: string | number | undefined;
        fileName: string | number | undefined;
        sheetName: string | number | undefined;
      };
    }[] = [];

    if (inputExcelInfo.selectYearMonthPattern === 'InHeader') {
      const inHeaderYearMonthInfo = inputExcelInfo.inHeaderYearMonthInfo;
      if (inHeaderYearMonthInfo == null) return output;

      const startColumnNumber = columnToNumber(inHeaderYearMonthInfo.startColumnNumber);
      const endColumnNumber = columnToNumber(inHeaderYearMonthInfo.endColumnNumber);
      const columnCount = endColumnNumber - startColumnNumber + 1;
      const yearMonths = DateService.getYearMonthsBetween(inHeaderYearMonthInfo.start, inHeaderYearMonthInfo.end);

      if (columnCount !== yearMonths.length) return output; // カラム数と年月数が一致しない場合はエラーを出したいがとりあえずスキップ
      //（以降の処理では、columnCountとyearMonths.lengthが一致することを前提としている）

      for (let i = startColumnNumber; i <= endColumnNumber; i++) {
        let headerName: string | undefined;
        switch (inputFileType) {
          case 'Inventory':
            headerName = inputExcelInfo.alternateHeaderInfos.find((info) => info.header === 'InventoryValue')?.value;
            break;
          case 'Arrival':
            headerName = inputExcelInfo.alternateHeaderInfos.find((info) => info.header === 'ArrivalValue')?.value;
            break;
          case 'SalesResults':
            headerName = inputExcelInfo.alternateHeaderInfos.find((info) => info.header === 'SalesResultsValue')?.value;
            break;
        }

        const combineHeaderKey = headerName + '_' + i;
        const combinedHeader = excelData?.[combineHeaderKey];
        if (headerName == null) continue;
        if (combinedHeader == null) continue;

        output.push({
          yearMonthString: DateService.convertIYearMonthToYYYYMM(yearMonths[i - startColumnNumber]),
          columnValue: combinedHeader,
        });
      }
    }

    if (inputExcelInfo.selectYearMonthPattern === 'Inlined') {
      const inlinedYearMonthHeaderName = inputExcelInfo.inlinedYearMonthHeaderName;
      if (inlinedYearMonthHeaderName == null || excelData?.[inlinedYearMonthHeaderName] == null) return output;
      const yearMonthStr = String(excelData[inlinedYearMonthHeaderName].value);
      let headerName: string | undefined;
      switch (inputFileType) {
        case 'Inventory':
          headerName = inputExcelInfo.alternateHeaderInfos.find((info) => info.header === 'InventoryValue')?.value;
          break;
        case 'Arrival':
          headerName = inputExcelInfo.alternateHeaderInfos.find((info) => info.header === 'ArrivalValue')?.value;
          break;
        case 'SalesResults':
          headerName = inputExcelInfo.alternateHeaderInfos.find((info) => info.header === 'SalesResultsValue')?.value;
          break;
      }
      if (headerName == null) return output;
      const columnValue = excelData?.[headerName];
      if (columnValue == null) return output;
      output.push({
        yearMonthString: yearMonthStr,
        columnValue: columnValue,
      });
    }

    return output;
  };

  /**
   * 内示エクセルの読み取り
   * @param file 対象Excelファイル
   * @param formArray 読み込ませるエクセルファイル
   * @param errorObj エラーをまとめるためのオブジェクト
   * @param fileName 対象シートの対象ページ文字列, デフォルトは0番目のページ
   * @param receivedYearMonth 内示の受領年月
   * @param extraMapFunc エクセルごとに、追加で読み込ませたいカラムがあれば ExcelMapper型を渡す
   */
  public readAnnounceExcel = async (
    file: File,
    formArray: Record<string, TranslateExcelBusiness>,
    errorObj: ExcelTranslateError[],
    fileName: string,
    receivedYearMonth: IYearMonth,
    ...extraMapFunc: AnnounceExcelMapper[]
  ) => {
    // TODO: ここだけLP書式のファイルなので既存処理使っているが、他の3つと合わせられるように修正したい

    const data = await fm.convertFileToBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const option: ExcelParseOption = addFileNameToInputExcelOption(fileName, sheetName, defaultAnnounceExcelParseOption);
    const excelData: ExcelRow[] = this.parseAnnounceExcelObjects(sheet, ANNOUNCE_EXCEL_START_CELL, '内示', option, errorObj); // 内示 NOTE: ここだけ、LP書式の入力ファイルなので固定値を使って読み込んでいる
    this.splitAnnounceExcelData(excelData, formArray, '内示', errorObj, receivedYearMonth, undefined, ...extraMapFunc);
    // ここで受領年月登録を行う
    Object.values(formArray).forEach((form) => {
      form.customerAnnounce?.map((announce) => (announce['manageInitiatedYearMonth'] = receivedYearMonth));
    });
  };

  /**
   * 日別内示エクセルの読み取り
   * @param file 対象Excelファイル
   * @param formArray 読み込ませるエクセルファイル
   * @param errorObj エラーをまとめるためのオブジェクト
   * @param fileName 対象シートの対象ページ文字列, デフォルトは0番目のページ
   * @param receivedYearMonth 内示の受領年月
   * @param clearMonthlyAnnounceOnce 月次内示の既存データを一度だけリセットする処理
   * @param extraMapFunc エクセルごとに、追加で読み込ませたいカラムがあれば ExcelMapper型を渡す
   */
  public readDailyAnnounceExcel = async (
    file: File,
    formArray: Record<string, TranslateExcelBusiness>,
    errorObj: ExcelTranslateError[],
    fileName: string,
    receivedYearMonth: IYearMonth,
    clearMonthlyAnnounceOnce?: () => void,
    ...extraMapFunc: AnnounceExcelMapper[]
  ) => {
    const data = await fm.convertFileToBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const q15Cell = sheet[DAILY_ANNOUNCE_START_DAY_CELL];
    const q15CellValue = bringCellValue(q15Cell);
    const normalizedQ15Value = String(q15CellValue ?? '').trim();

    // 日別内示の開始日は「1日」を許容する。
    // セル値が日付シリアル値（例: 2026/1/1）で来る場合にも対応する。
    const isValidQ15StartDay = (() => {
      const numericValue = Number(normalizedQ15Value);
      if (!Number.isNaN(numericValue)) {
        if (numericValue === 1) return true;
        const serialDate = DateService.convertExcelSerialToDate(numericValue);
        if (serialDate) return serialDate.day === 1;
      }

      const displayValue = String(q15Cell?.w ?? normalizedQ15Value).trim();
      const dateMatch = displayValue.match(/(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
      if (dateMatch) {
        const [, , , day] = dateMatch;
        return Number(day) === 1;
      }

      const parsedDate = new Date(displayValue);
      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate.getDate() === 1;
      }

      const dayMatch = displayValue.match(/^(\d{1,2})(日)?$/);
      if (dayMatch) {
        const [, day] = dayMatch;
        return Number(day) === 1;
      }

      return false;
    })();
    if (!isValidQ15StartDay) {
      errorObj.push({
        key: 'DailyAnnounceR15StartDay',
        errCode: 'InappropriateType',
        errorFileName: {
          inputFileName: fileName,
        },
        preCellNumber: '',
        errorCellNumber: {
          inputCellNumber: DAILY_ANNOUNCE_START_DAY_CELL,
          registerCellNumber: '',
        },
        errorString: `日別内示はセル${DAILY_ANNOUNCE_START_DAY_CELL}を必ず1日としてください。`,
      });
      return;
    }

    const option: ExcelParseOption = addFileNameToInputExcelOption(fileName, sheetName, defaultAnnounceExcelParseOption);
    const excelData: ExcelRow[] = this.parseAnnounceExcelObjects(sheet, ANNOUNCE_EXCEL_START_CELL, '日別内示', option, errorObj);

    clearMonthlyAnnounceOnce?.();

    // 日別内示データを読み込む
    this.splitAnnounceExcelData(excelData, formArray, '日別内示', errorObj, receivedYearMonth, undefined, ...extraMapFunc);
    Object.values(formArray).forEach((form) => {
      form.customerAnnounceDaily?.map((announce) => (announce['manageInitiatedYearMonth'] = receivedYearMonth));
    });

    // N-Q列の月次内示データも読み込む
    this.splitAnnounceExcelData(
      excelData,
      formArray,
      '日別内示',
      errorObj,
      receivedYearMonth,
      { skipDuplicateCheck: true },
      this.ANNOUNCE_MAPPER
    );
    Object.values(formArray).forEach((form) => {
      form.customerAnnounce?.map((announce) => (announce['manageInitiatedYearMonth'] = receivedYearMonth));
    });
  };

  /**
   * 販売実績エクセルの読み取り
   * @param file 対象Excelファイル
   * @param formArray 読み込ませるエクセルファイル
   * @param inputExcelInfo エクセル取り込みの情報
   * @param allowDuplicateHeaderName ヘッダーの重複を許可するヘッダー名
   * @param errorObj エラーを溜めておくオブジェクト
   * @param fileName 対象シートの対象ページ文字列, デフォルトは0番目のページ
   * @param extraMapFunc エクセルごとに、追加で読み込ませたいカラムがあれば ExcelMapper型を渡す
   */
  public readSalesResultExcel = async (
    file: File,
    formArray: Record<string, TranslateExcelBusiness>,
    inputExcelInfo: RegisterExcelInfo,
    allowDuplicateHeaderName: string,
    errorObj: ExcelTranslateError[],
    fileName: string,
    ...extraMapFunc: newExcelMapper[]
  ) => {
    // console.log('readExcel', file, formArray, name, extraMapFunc);
    const data = await fm.convertFileToBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const option: ExcelParseOption = addFileNameToInputExcelOption(fileName, sheetName, defaultSalesResultExcelParseOption);

    const excelData: ExcelRow[] = this.parseExcelObjects(sheet, inputExcelInfo.headerLineNumber, allowDuplicateHeaderName, option); // 内示

    this.splitExcelData(excelData, formArray, '販売実績', errorObj, inputExcelInfo, ...extraMapFunc);
  };

  /**
   * 入荷エクセルの読み取り
   * @param file 対象Excelファイル
   * @param formArray 読み込ませるエクセルファイル
   * @param inputExcelInfo エクセル取り込みの情報
   * @param allowDuplicateHeaderName ヘッダーの重複を許可するヘッダー名
   * @param errorObj エラーを溜めておくオブジェクト
   * @param fileName 対象シートの対象ページ文字列, デフォルトは0番目のページ
   * @param extraMapFunc エクセルごとに、追加で読み込ませたいカラムがあれば ExcelMapper型を渡す
   */
  public readArrivalExcel = async (
    file: File,
    formArray: Record<string, TranslateExcelBusiness>,
    inputExcelInfo: RegisterExcelInfo,
    allowDuplicateHeaderName: string,
    errorObj: ExcelTranslateError[],
    fileName: string,
    ...extraMapFunc: newExcelMapper[]
  ) => {
    // console.log('readExcel', file, formArray, name, extraMapFunc);
    const data = await fm.convertFileToBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const option: ExcelParseOption = addFileNameToInputExcelOption(fileName, sheetName, defaultArrivalExcelParseOption);

    const excelData: ExcelRow[] = this.parseExcelObjects(sheet, inputExcelInfo.headerLineNumber, allowDuplicateHeaderName, option); // 内示

    this.splitExcelData(excelData, formArray, '入荷', errorObj, inputExcelInfo, ...extraMapFunc);
  };

  /**
   * 在庫エクセルの読み取り
   * @param file 対象Excelファイル
   * @param formArray 読み込ませるエクセルファイル
   * @param inputExcelInfo エクセル取り込みの情報
   * @param allowDuplicateHeaderName ヘッダーの重複を許可するヘッダー名
   * @param errorObj エラーを溜めておくオブジェクト
   * @param fileName 対象シートの対象ページ文字列, デフォルトは0番目のページ
   * @param extraMapFunc エクセルごとに、追加で読み込ませたいカラムがあれば ExcelMapper型を渡す
   */
  public readInventoryExcel = async (
    file: File,
    formArray: Record<string, TranslateExcelBusiness>,
    inputExcelInfo: RegisterExcelInfo,
    allowDuplicateHeaderName: string,
    errorObj: ExcelTranslateError[],
    fileName: string,
    ...extraMapFunc: newExcelMapper[]
  ) => {
    const data = await fm.convertFileToBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const option: ExcelParseOption = addFileNameToInputExcelOption(fileName, sheetName, defaultInventoryExcelParseOption);

    const excelData: ExcelRow[] = this.parseExcelObjects(sheet, inputExcelInfo.headerLineNumber, allowDuplicateHeaderName, option);
    this.splitExcelData(excelData, formArray, '在庫', errorObj, inputExcelInfo, ...extraMapFunc);
  };

  /**
   * エクセルファイルをIBusiness型に落とし込む
   * @param excelData 解析済みエクセルデータ
   * @param formArray IBusinessをラップしたエクセル転記情報の配列
   * @param readExcelName 読み込んでいるファイルの名前
   * @param errorObj エラー情報をまとめるためのオブジェクト
   * @param inputHeaderInfo エクセルのヘッダー読み替え情報
   * @param extraMapFunc 新たにMapperがある時に利用する
   */
  private splitExcelData = (
    excelData: ExcelRow[],
    formArray: Record<string, TranslateExcelBusiness>,
    readExcelName: TranslateKeyName,
    errorObj: ExcelTranslateError[],
    inputHeaderInfo: RegisterExcelInfo,
    ...extraMapFunc: newExcelMapper[]
  ) => {
    /**
     * 転記元のセルが設定されているか判定する
     * @param header - 判定対象のヘッダー
     */
    const isConfiguredHeader = (header: AlterableHeader): boolean => {
      const headerInfo = inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === header);
      return headerInfo != null && headerInfo.unused !== true && headerInfo.value.trim() !== '';
    };

    // ヘッダー選択必須項目について
    const useWarehouseNumber = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'WarehouseNumber')?.unused;
    const useBusinessNumber = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'BusinessNumber')?.unused;
    const useSalesDestinationNumber = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SalesDestinationNumber')
      ?.unused;
    const useCustomerNumber = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'CustomerNumber')?.unused;
    const useSupplierNumber = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SupplierNumber')?.unused;
    const usePackage = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'Package')?.unused;

    // ヘッダー選択任意項目について
    const useWarehouse = isConfiguredHeader('Warehouse');
    const useBusinessName = isConfiguredHeader('BusinessName');
    const useSalesDestinationName = isConfiguredHeader('SalesDestinationName');
    const useCustomerName = isConfiguredHeader('CustomerName');
    const useSupplierName = isConfiguredHeader('SupplierName');
    const useManufacturer = isConfiguredHeader('Manufacturer');
    const useEndUser = isConfiguredHeader('EndUser');
    const useOrderPackage = !inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === ('orderPackage' as any))?.unused;

    // 基本的な要素＋salesPositionに関してはここ、それ以外に関してはExtraMapにて処理を実行する
    excelData.forEach((data, idx) => {
      // if (idx !== 0) formArray.push(JSON.parse(JSON.stringify(newDefaultData)));
      try {
        // MVP ver1.0.0
        // 在庫表には置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDが存在する
        // 内示は内示エクセルの置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDと在庫表が合致する行に転記
        // 販売実績は販売実績エクセルの置き場CD、商品CD、容量、仕入先CD、送り先CD、売上先CDと在庫表が合致する行に転記
        // 入荷は入荷エクセルの置き場CD、商品CD、容量、仕入先CDと在庫表が合致する集計行に転記
        // 在庫は在庫エクセルの置き場CD、商品CD、容量、仕入先CDと在庫表が合致する集計行に転記
        // ~名(CDと1対１対応)は、在庫表の値を採用する（転記には用いない）

        // 選択必須項目のヘッダー名を取得
        const warehouseNumberHeaderName =
          inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'WarehouseNumber')?.value ?? '置場CD';
        const businessNumberHeaderName =
          inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'BusinessNumber')?.value ?? '商品CD';
        const packageHeaderName = inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'Package')?.value ?? '容量';
        const salesDestinationNumberHeaderName =
          inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SalesDestinationNumber')?.value ?? '送り先CD';
        const customerNumberHeaderName =
          inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'CustomerNumber')?.value ?? '売上先CD';
        const supplierNumberHeaderName =
          inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SupplierNumber')?.value ?? '仕入先CD';

        // 選択任意項目のヘッダー名を取得 (undefinedの場合は'----=========----'を代入)
        const warehouseHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'Warehouse')?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'Warehouse')?.value ?? '----=========----'
          : '----=========----';
        const businessNameHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'BusinessName')?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'BusinessName')?.value ?? '----=========----'
          : '----=========----';
        const salesDestinationNameHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SalesDestinationName')
          ?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SalesDestinationName')?.value ?? '----=========----'
          : '----=========----';
        const customerNameHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'CustomerName')?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'CustomerName')?.value ?? '----=========----'
          : '----=========----';
        const supplierNameHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SupplierName')?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'SupplierName')?.value ?? '----=========----'
          : '----=========----';
        const manufacturerHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'Manufacturer')?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'Manufacturer')?.value ?? '----=========----'
          : '----=========----'; // 製造社ヘッダー名を取得
        const endUserHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'EndUser')?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === 'EndUser')?.value ?? '----=========----'
          : '----=========----';
        const orderPackageHeaderName = !!inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === ('orderPackage' as any))?.value
          ? inputHeaderInfo.alternateHeaderInfos.find((info) => info.header === ('orderPackage' as any))?.value ?? '----=========----'
          : '----=========----';

        let business: TranslateExcelBusiness = JSON.parse(JSON.stringify(newDefaultData));

        business.warehouse = useWarehouse ? filterStringNumberUndefinedToString(data[warehouseHeaderName]?.value) : '-';
        business.warehouseNumber = useWarehouseNumber ? filterStringNumberUndefinedToString(data[warehouseNumberHeaderName]?.value) : '-';

        business.businessName = useBusinessName ? filterStringNumberUndefinedToString(data[businessNameHeaderName]?.value) : '-';
        business.businessNumber = useBusinessNumber ? filterStringNumberUndefinedToString(data[businessNumberHeaderName]?.value) : '-';

        business.package = usePackage
          ? filterStringNumberUndefinedToString(data[packageHeaderName]?.value).parseNumberStringAndRemoveString()
          : '0.01'; // NOTE: 容量に関しては、文字が含まれていた場合「0.01」として扱う

        business.salesDestinationName = useSalesDestinationName
          ? filterStringNumberUndefinedToString(data[salesDestinationNameHeaderName]?.value)
          : '-';
        business.salesDestinationNumber = useSalesDestinationNumber
          ? filterStringNumberUndefinedToString(data[salesDestinationNumberHeaderName]?.value)
          : '-';

        business.customerName = useCustomerName ? filterStringNumberUndefinedToString(data[customerNameHeaderName]?.value) : '-';
        business.customerNumber = useCustomerNumber ? filterStringNumberUndefinedToString(data[customerNumberHeaderName]?.value) : '-';

        business.supplierName = useSupplierName ? filterStringNumberUndefinedToString(data[supplierNameHeaderName]?.value) : '-';
        business.supplierNumber = useSupplierNumber ? filterStringNumberUndefinedToString(data[supplierNumberHeaderName]?.value) : '-';

        business.manufacturer = useManufacturer ? filterStringNumberUndefinedToString(data[manufacturerHeaderName]?.value) : '-';
        business.endUser = useEndUser ? filterStringNumberUndefinedToString(data[endUserHeaderName]?.value) : '-';
        business.orderPackage = useOrderPackage
          ? Number(filterStringNumberUndefinedToString(data[orderPackageHeaderName]?.value)) || undefined
          : undefined;

        // IDは上記の複合キーとする
        business.translateBusinessId = getTranslateBusinessIdManager().generateBusinessIdFromExcelInput(business, readExcelName);
        const businessForUseLog = simpleCopy(business); // ログを取るためだけに、行の情報を持っておく

        // 有効な取引の基本パラメータに関して、validateを行う
        const useValidate = isValidTranslateBusiness(business);
        if (useValidate) {
          const propertyNames = {};

          const mappings: [boolean, string, string][] = [
            [useWarehouseNumber, '置場CD', warehouseNumberHeaderName],
            [useBusinessNumber, '商品CD', businessNumberHeaderName],
            [usePackage, '容量', packageHeaderName],
            [useSalesDestinationNumber, '送り先CD', salesDestinationNumberHeaderName],
            [useCustomerNumber, '売上先CD', customerNumberHeaderName],
            [useSupplierNumber, '仕入先CD', supplierNumberHeaderName],
          ];

          for (const [useFlag, key, value] of mappings) {
            if (useFlag) {
              propertyNames[key] = value;
            }
          }
          newValidateInputFoundationParameters(business, data, propertyNames, errorObj);
        }

        if (formArray[business.translateBusinessId]) {
          // 同一取引が登録されている入力側のエクセルファイル名
          let registeredExcelInfo: ExcelRegisteredFileInfo = {
            registeredFileName: '',
            registeredSheetName: '',
            registeredCellNumber: '',
          };
          const excelInfoLength = formArray[business.translateBusinessId].excelInfos?.length;
          if (excelInfoLength && readExcelName === '内示') {
            registeredExcelInfo = {
              registeredFileName: formArray[business.translateBusinessId].excelInfos![excelInfoLength - 1].fileName,
              registeredSheetName: String(formArray[business.translateBusinessId].excelInfos![excelInfoLength - 1].sheetNumber),
              registeredCellNumber: String(formArray[business.translateBusinessId].excelInfos![excelInfoLength - 1].cellNumber),
            };
          }
          // 内示、販売実績のとき、同一の取引が複数入力エクセル内にあった場合に取引重複エラーとして登録する
          if (
            getTranslateBusinessIdManager().isFileDuplicated(
              readExcelName,
              formArray[business.translateBusinessId],
              business.translateBusinessId
            )
          ) {
            registerErrorInformation(
              errorObj,
              data,
              businessForUseLog,
              business.translateBusinessId,
              '取引情報が重複している',
              registeredExcelInfo
            );
          }
          // 既に同一のアイテムを読み込んでいる(同一取引が重複した)場合, 既存のアイテムで更新
          business = formArray[business.translateBusinessId];
        }
        extraMapFunc.forEach((func) => func(business, data, inputHeaderInfo, errorObj, useValidate));
        extraMapFunc.forEach((func) => func(businessForUseLog, data, inputHeaderInfo, errorObj, false));
        if (!business?.excelInfos) {
          business.excelInfos = [];
        }
        business.excelInfos.push({
          cellNumber: String(data[businessNumberHeaderName]?.address),
          sheetNumber: String(data[businessNumberHeaderName]?.sheetName),
          fileName: String(data[businessNumberHeaderName]?.fileName),
          rowObjectInfo: simpleCopy(businessForUseLog),
        });
        // 最後に、formArrayを更新
        formArray[business.translateBusinessId] = business;
        // キーが同じものはまとめる処理
        // 各々のインプット要素を受け取る
      } catch (error) {
        consoleObject(error);
        throw error;
      }
    });
  };

  /**
   * 表の位置が不定なExcelの中から、ExcelRow[]を作成する関数
   * @param workSheet ワークシートの情報
   * @param startCellRow 表が始まっているセルの行番号
   * @param allowDuplicateHeaderName 重複を許容するヘッダー名
   * @param option parseExcelObjectsで色々やるためのオプション
   */
  public parseExcelObjects = (
    workSheet: XLSX.WorkSheet,
    startCellRow: number,
    allowDuplicateHeaderName: string,
    option?: ExcelParseOption
  ): ExcelRow[] => {
    //セルの範囲
    const output: ExcelRow[] = [];
    const headerStrings: string[] = []; // 重複を許可したいため配列にしてます
    const range = workSheet['!ref'];
    let findHeaderRow = -1;
    const ignoreRows: Array<number> = [];

    // convert index
    for (const row of option ? option.ignoreRow : []) {
      ignoreRows.push(XLSX.utils.decode_cell(row).r);
    }
    if (!range) return [];
    const rangeVal = XLSX.utils.decode_range(range);

    // workSheet['!ref']がpaddingのような切り取り方をしているのでとりあえずmax-1行目までの抽出としています
    for (let r = 0; r <= rangeVal.e.r; r++) {
      if (ignoreRows.includes(r)) continue;
      const rowInfo: ExcelRow = Object.assign({}, {});
      let updatedRow = false;
      for (let c = 0; c <= rangeVal.e.c; c++) {
        const adr = XLSX.utils.encode_cell({ c: c, r: r });
        const cell = workSheet[adr];
        const cellValue = bringCellValue(cell);
        // 対象範囲
        if (startCellRow - 1 <= r) {
          if (findHeaderRow == -1 || findHeaderRow == r) {
            headerStrings.push(cellValue); // ヘッダーの追加
            findHeaderRow = r; // 最初の１行目だけ見たい（Header)
            // console.log(`${adr} type:${cell.t} value:${cell.v} text:${cell.w}`);
            continue;
          }
          // 空として登録されてるヘッダーについては無視する(セルが結合等されていてindexずれしていたのがうざかったのでこうしています)
          // NOTE: ここ（分岐）、セル結合を考慮して何か処理を入れないといけない気がしているがうまくいかないのでとりあえずこうしてます
          // columnIndexは1-indexとして扱いたい
          const rawKey = headerStrings[c];
          // console.log('チェック', headerStrings, baseColumn + c, { cellValue: cellValue, rawKey });
          if (allowDuplicateHeaderName === rawKey) {
            // 重複を許容するヘッダー名の場合は、重複しないように加工して追加する
            const duplicateKey = `${rawKey}_${c + 1}`; // エクセルの列数を_で区切って追加
            rowInfo[duplicateKey] = {
              value: cellValue,
              address: adr,
              sheetName: option?.sheetName,
              fileName: option?.fileName,
            };
          } else if (!rowInfo?.[rawKey]) {
            // 既に同じキーが存在している場合は無視する
            rowInfo[rawKey] = {
              value: cellValue,
              address: adr,
              sheetName: option?.sheetName,
              fileName: option?.fileName,
            };
          }
          updatedRow = true; // 1つでもデータが入っていたら更新フラグを立てる
        }
      }
      if (updatedRow) {
        output.push(rowInfo);
      }
    }
    return output;
  };
}
